import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '@/shared/api/client'
import { endpoints } from '@/shared/api/endpoints'
import { useAppStore } from '@/shared/stores/appStore'
import { leagueKeys } from '@/features/league/queries'

// Admin surface for Sobrevivente only. Deliberately excludes every fantasy action —
// no player stats, no Process, no prices, no rosters — because the survivor pipeline
// needs nothing but match scores, and those are the expensive API calls we no longer pay for.

export const survivorAdminKeys = {
  all:          ['survivor-admin'] as const,
  competitions: () => [...survivorAdminKeys.all, 'competitions'] as const,
  gameweeks:    (competitionId: string) =>
    [...survivorAdminKeys.all, 'gameweeks', competitionId] as const,
  matches:      (gameweekId: string) =>
    [...survivorAdminKeys.all, 'matches', gameweekId] as const,
  schedules:    (gameweekId: string) =>
    [...survivorAdminKeys.all, 'schedules', gameweekId] as const,
}

// ── Competitions ──────────────────────────────────────────────────────────────

export type CompetitionFormat = 'KNOCKOUT' | 'LEAGUE'

export type AdminCompetition = {
  id:               string
  name:             string
  slug:             string
  sport:            string
  isActive:         boolean
  startsAt:         string
  endsAt:           string
  format:           CompetitionFormat
  externalLeagueId: string | null
  season:           number | null
}

export async function fetchCompetitions(): Promise<AdminCompetition[]> {
  return apiClient.get<AdminCompetition[]>(endpoints.competitions.list())
}

export function useAdminCompetitions() {
  return useQuery({
    queryKey:  survivorAdminKeys.competitions(),
    queryFn:   fetchCompetitions,
    staleTime: 30_000,
  })
}

export type CreateCompetitionInput = {
  name:              string
  slug:              string
  format:            CompetitionFormat
  externalLeagueId?: string
  season?:           number
  startsAt:          string
  endsAt:            string
}

function useInvalidateCompetitions() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: survivorAdminKeys.all })
    // The competition picker elsewhere in the app reads the same list.
    void qc.invalidateQueries({ queryKey: leagueKeys.all })
  }
}

export function useCreateCompetition() {
  const invalidate = useInvalidateCompetitions()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (input: CreateCompetitionInput) =>
      apiClient.post<AdminCompetition>(endpoints.admin.createCompetition(), input),
    onSuccess: (competition) => {
      invalidate()
      addToast({
        type:  'success',
        title: 'Competição criada',
        body:  `${competition.name} — importe os times em seguida.`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao criar competição', body: err.message })
    },
  })
}

export type UpdateCompetitionInput = {
  name?:             string
  externalLeagueId?: string
  season?:           number
  startsAt?:         string
  endsAt?:           string
  isActive?:         boolean
}

export function useUpdateCompetition() {
  const invalidate = useInvalidateCompetitions()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ competitionId, input }: {
      competitionId: string
      input:         UpdateCompetitionInput
    }) =>
      apiClient.patch<AdminCompetition>(
        endpoints.admin.updateCompetition(competitionId), input),
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', title: 'Competição atualizada' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao atualizar competição', body: err.message })
    },
  })
}

// ── Import: teams + calendar ──────────────────────────────────────────────────

export type SyncTeamsResult = {
  competitionId:  string
  teamsFromApi:   number
  teamsCreated:   number
  teamsUpdated:   number
  groupsAssigned: number
  warnings:       string[]
}

export function useImportTeams() {
  const invalidate = useInvalidateCompetitions()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (competitionId: string) =>
      apiClient.post<SyncTeamsResult>(endpoints.admin.syncTeams(competitionId), {}),
    onSuccess: (result) => {
      invalidate()
      addToast({
        type:  'success',
        title: 'Times importados',
        body:  `${result.teamsCreated} criados · ${result.teamsUpdated} atualizados`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao importar times', body: err.message })
    },
  })
}

export type SyncScheduleResult = {
  competitionId:    string
  fixturesFromApi:  number
  gameweeksCreated: number
  matchesCreated:   number
  matchesUpdated:   number
  warnings:         string[]
}

export function useImportCalendar() {
  const invalidate = useInvalidateCompetitions()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (competitionId: string) =>
      apiClient.post<SyncScheduleResult>(endpoints.admin.syncSchedule(competitionId), {}),
    onSuccess: (result) => {
      invalidate()
      addToast({
        type:  'success',
        title: 'Calendário importado',
        body:  `${result.gameweeksCreated} rodadas · ${result.matchesCreated} partidas`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao importar calendário', body: err.message })
    },
  })
}

// ── Gameweeks ─────────────────────────────────────────────────────────────────

export type GameweekStatus = 'UPCOMING' | 'ACTIVE' | 'LOCKED' | 'FINALIZED'

export type SurvivorAdminGameweek = {
  id:                    string
  number:                number
  phase:                 string
  status:                GameweekStatus
  firstMatchAt:          string
  lastMatchEndsAt:       string
  canHaveExtraTime:      boolean
  scoresFinalizedAt:     string | null
  transferWindowOpensAt: string | null
  scoresComputed:        boolean
}

export async function fetchGameweeks(competitionId: string): Promise<SurvivorAdminGameweek[]> {
  return apiClient.get<SurvivorAdminGameweek[]>(endpoints.gameweeks.list(competitionId))
}

export function useSurvivorAdminGameweeks(competitionId: string | null) {
  return useQuery({
    queryKey:  survivorAdminKeys.gameweeks(competitionId ?? ''),
    queryFn:   () => fetchGameweeks(competitionId as string),
    enabled:   !!competitionId,
    staleTime: 30_000,
  })
}

// ── Matches (round detail + manual score entry) ────────────────────────────────

export type AdminMatchTeam = {
  id:           string
  name:         string
  code:         string
  groupCode:    string | null
  isEliminated: boolean
}

export type AdminMatch = {
  id:                  string
  externalId:          string
  status:              'SCHEDULED' | 'LIVE' | 'HALFTIME' | 'FINISHED' | 'POSTPONED'
  kickoffAt:           string
  homeTeam:            AdminMatchTeam | null
  awayTeam:            AdminMatchTeam | null
  homeTeamPlaceholder: string | null
  awayTeamPlaceholder: string | null
  homeScore:           number | null
  awayScore:           number | null
  isThirdPlaceMatch:   boolean
}

export function useGameweekMatches(gameweekId: string | null) {
  return useQuery({
    queryKey:  survivorAdminKeys.matches(gameweekId ?? ''),
    queryFn:   () => apiClient.get<AdminMatch[]>(endpoints.matches.list(gameweekId as string)),
    enabled:   !!gameweekId,
    staleTime: 15_000,
  })
}

// Every gameweek action changes what participants see (available teams, picks,
// standings), so the survivor caches go too — not just the admin ones.
function useInvalidateGameweekDependents() {
  const qc = useQueryClient()
  return () => {
    void qc.invalidateQueries({ queryKey: survivorAdminKeys.all })
    void qc.invalidateQueries({ queryKey: ['survivor'] })
    void qc.invalidateQueries({ queryKey: leagueKeys.all })
  }
}

// ── Operational actions: Lock → Results → Finalize ────────────────────────────

export type LockGameweekResult = void

export type SyncResultsResult = {
  competitionId:       string
  gameweekId:          string | null
  fixturesFromApi:     number
  matchesUpdated:      number
  matchesPostponed:    number
  matchesStillPending: number
  allMatchesFinished:  boolean
  warnings:            string[]
}

export type FinalizeGameweekResult = { gameweekId: string; finalizedAt: string }

export type ResetGameweekResult = {
  gameweekId:           string
  wasFinalized:         boolean
  squadScoresDeleted:   number
  playerScoresDeleted:  number
  h2hFixturesReset:     number
  h2hLeaguesRecomputed: number
  // Fetched match scores dropped with them, so nothing keeps reading the round as decided.
  // They have to be pulled again — Finalizar refuses a survivor round with no results.
  matchResultsCleared:  number
  // Sobrevivente rollback. Picks resolved by this round's Finalize go back to PENDING and
  // the eliminations/champions it produced are undone; lock-time W.O. forfeits are kept.
  survivorPicksReverted:         number
  survivorParticipationsRevived: number
  survivorGamesReopened:         number
}

export type SurvivorAdminAction =
  | { kind: 'lock';     result: LockGameweekResult }
  | { kind: 'results';  result: SyncResultsResult }
  | { kind: 'finalize'; result: FinalizeGameweekResult }
  | { kind: 'reset';    result: ResetGameweekResult }

export function useLockGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<LockGameweekResult>(endpoints.admin.lockGameweek(gameweekId), {}),
    onSuccess: () => {
      invalidate()
      addToast({
        type:  'success',
        title: 'Rodada bloqueada',
        body:  'Quem não escolheu time foi eliminado por W.O.',
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao bloquear', body: err.message })
    },
  })
}

export function useSyncGameweekResults() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<SyncResultsResult>(endpoints.admin.syncGameweekResults(gameweekId), {}),
    onSuccess: (result) => {
      invalidate()
      addToast({
        type:  result.allMatchesFinished ? 'success' : 'info',
        title: result.allMatchesFinished
          ? 'Resultados completos'
          : 'Resultados parciais',
        body:  result.allMatchesFinished
          ? `${result.matchesUpdated} placar(es) gravado(s) — a rodada pode ser finalizada.`
          : `${result.matchesUpdated} gravado(s) · ${result.matchesStillPending} partida(s) ainda sem resultado.`,
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao buscar resultados', body: err.message })
    },
  })
}

export function useFinalizeGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<FinalizeGameweekResult>(endpoints.admin.finalizeGameweek(gameweekId), {}),
    onSuccess: () => {
      invalidate()
      addToast({
        type:  'success',
        title: 'Rodada finalizada',
        body:  'Palpites resolvidos e eliminações aplicadas.',
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao finalizar', body: err.message })
    },
  })
}

export function useResetGameweek() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (gameweekId: string) =>
      apiClient.post<ResetGameweekResult>(endpoints.admin.resetGameweek(gameweekId), {}),
    onSuccess: (result) => {
      invalidate()
      addToast({
        type:  'success',
        title: 'Rodada resetada',
        body:  `Voltou para LOCKED · ${result.survivorPicksReverted} palpite(s) e `
             + `${result.survivorParticipationsRevived} participante(s) revertidos, `
             + `${result.matchResultsCleared} placar(es) apagado(s) — `
             + 'busque os resultados e finalize novamente.',
      })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao resetar', body: err.message })
    },
  })
}

export type SetMatchResultResult = {
  matchId:         string
  gameweekId:      string
  status:          string
  homeScore:       number
  awayScore:       number
  wentToExtraTime: boolean
}

export function useSetMatchResult() {
  const invalidate = useInvalidateGameweekDependents()
  const addToast   = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: ({ matchId, homeScore, awayScore, wentToExtraTime }: {
      matchId:          string
      homeScore:        number
      awayScore:        number
      wentToExtraTime?: boolean
    }) =>
      apiClient.patch<SetMatchResultResult>(
        endpoints.admin.setMatchResult(matchId),
        { homeScore, awayScore, wentToExtraTime: wentToExtraTime ?? false },
      ),
    onSuccess: () => {
      invalidate()
      addToast({ type: 'success', title: 'Placar salvo' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao salvar placar', body: err.message })
    },
  })
}

// ── Gameweek action scheduler ─────────────────────────────────────────────────
// Fires a survivor action through Hangfire on a cadence. Only three actions make
// sense here — Lock, Buscar resultados, Finalizar. Fetch stats and Process are
// fantasy-only and deliberately absent.

export type SurvivorScheduleAction = 'LOCK' | 'RESULTS' | 'FINALIZE'
export type ScheduleMode           = 'ONCE' | 'INTERVAL' | 'WINDOW' | 'CRON'
export type ScheduleIntervalUnit   = 'MINUTES' | 'HOURS'
export type ScheduleStatus         = 'SCHEDULED' | 'DONE' | 'CANCELLED' | 'FAILED'

export type ScheduledJob = {
  id:              string
  gameweekId:      string
  action:          string
  mode:            ScheduleMode
  runAt:           string | null
  cron:            string | null
  intervalMinutes: number | null
  windowStart:     string | null
  windowEnd:       string | null
  status:          ScheduleStatus
  runCount:        number
  lastRunAt:       string | null
  lastError:       string | null
  createdAt:       string
}

// Optional keys are omitted rather than set to undefined (exactOptionalPropertyTypes).
export type CreateScheduleInput = {
  action:         SurvivorScheduleAction
  mode:           ScheduleMode
  runAt?:         string
  intervalValue?: number
  intervalUnit?:  ScheduleIntervalUnit
  windowStart?:   string
  windowEnd?:     string
  cron?:          string
}

export function useGameweekSchedules(gameweekId: string | null) {
  return useQuery({
    queryKey:  survivorAdminKeys.schedules(gameweekId ?? ''),
    queryFn:   () => apiClient.get<ScheduledJob[]>(
      endpoints.admin.schedules.list(gameweekId as string)),
    enabled:   !!gameweekId,
    staleTime: 15_000,   // short so runCount / status refresh as jobs fire
  })
}

export function useCreateSchedule(gameweekId: string) {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (input: CreateScheduleInput) =>
      apiClient.post<ScheduledJob>(endpoints.admin.schedules.create(gameweekId), input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: survivorAdminKeys.schedules(gameweekId) })
      addToast({ type: 'success', title: 'Agendamento criado' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao agendar', body: err.message })
    },
  })
}

export function useCancelSchedule(gameweekId: string) {
  const qc       = useQueryClient()
  const addToast = useAppStore(s => s.addToast)
  return useMutation({
    mutationFn: (scheduleId: string) =>
      apiClient.delete<void>(endpoints.admin.schedules.cancel(scheduleId)),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: survivorAdminKeys.schedules(gameweekId) })
      addToast({ type: 'success', title: 'Agendamento cancelado' })
    },
    onError: (err: Error) => {
      addToast({ type: 'error', title: 'Erro ao cancelar', body: err.message })
    },
  })
}
